import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, LifeBuoy, AlarmClock, FileSignature } from 'lucide-react';

const ANNUITY_SPECIALISTS = [
  { name: 'Tom Bradley', title: 'ANNUITY SPECIALIST', email: 'tom@aegisfinancial.com' },
  { name: 'Dan Winders', title: 'ANNUITY SPECIALIST', email: 'dan@aegisfinancial.com' },
];

const CARRIER_CONTACTS = [
  { carrier: 'National Life Group', phone: '1-800-732-8939', email: 'Contracting@nationallife.com' },
  { carrier: 'Americo', phone: '(800) 395-9238', email: 'agent.services@americo.com' },
  { carrier: 'Aflac', phone: '(833) 504-0336', email: 'information@aflac.aetna.com' },
  { carrier: 'Aetna', phone: '(866) 272-6630', email: 'AetSSIinformation@aetna.com' },
  { carrier: 'Ethos', phone: '(415) 915-0665', email: 'agents@getethos.com' },
  { carrier: 'Royal Neighbors', phone: '(800) 770-4561', email: 'agentcontract@royalneighbors.org', option: 'Option 3' },
  { carrier: 'Transamerica', phone: '(866) 834-0436', email: 'tlp-crcontractadmin@transamerica.com' },
  { carrier: 'United Home Life (UHL)', phone: '(800) 428-3001', email: 'UHL.Contracting@unitedhomelife.com', option: 'Option 3' },
  { carrier: 'American-Amicable', phone: '(254) 297-2777', email: 'contracting@aatx.com' },
  { carrier: 'Kansas City Life', phone: '(800) 572-2467', email: 'carol.palermo@peoplesprotection.net' },
];

const CONTRACTING_SUPPORT = [
  {
    name: 'Rachel Root',
    title: 'ANNUITY CONTRACTS',
    detail: 'American Equity • American National\nFidelity & Guaranty Life',
    email: 'Rachel@aegisfinancial.com',
  },
  {
    name: 'Nicole Skattum',
    title: 'LIFE CONTRACTS',
    detail: 'Fidelity & Guaranty Life\ncontracting assistance',
    email: 'nicole@aegisfinancial.com',
  },
];

const initials = (name) => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

const PersonCard = ({ person, testId }) => (
  <Card className="bg-white overflow-hidden" data-testid={testId}>
    <CardContent className="flex items-center gap-4 p-5">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F6C561] to-[#D89E2F] text-lg font-extrabold text-white shadow-md">
        {initials(person.name)}
      </div>
      <div className="min-w-0">
        <h3 className="font-['Manrope'] text-lg font-extrabold text-[#2B3674]">{person.name}</h3>
        <p className="text-[11px] font-bold tracking-[0.12em] text-[#B47E14]">{person.title}</p>
        {person.detail && (
          <p className="mt-1 whitespace-pre-line text-xs text-[#707EAE]">{person.detail}</p>
        )}
        <a
          href={`mailto:${person.email}`}
          className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3FBF] hover:underline"
          data-testid={`${testId}-email`}
        >
          <Mail className="h-3.5 w-3.5" />
          {person.email}
        </a>
      </div>
    </CardContent>
  </Card>
);

export default function Support() {
  return (
    <div className="max-w-[1100px] mx-auto space-y-6" data-testid="support-page">
      {/* Hero banner */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#0B1437] via-[#111C44] to-[#1B254B] p-8"
        data-testid="support-hero"
      >
        <div className="pointer-events-none absolute -right-12 -top-16 h-52 w-52 rounded-full bg-[#F6C561]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-[#2447D6]/40 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F6C561] to-[#D89E2F] shadow-lg">
            <LifeBuoy className="h-7 w-7 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              IMPORTANT CONTACTS
            </h1>
            <p className="mt-1 text-sm font-medium tracking-[0.14em] text-[#F6C561]">
              YOUR QUICK-REFERENCE SUPPORT DIRECTORY
            </p>
          </div>
        </div>
      </motion.div>

      {/* Annuity Specialists */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-['Manrope'] text-lg font-extrabold tracking-wide text-[#B47E14]">
            ANNUITY SPECIALISTS
          </h2>
          <p className="text-xs text-[#707EAE]">
            Illustrations • Products • Product knowledge • High-level annuity questions
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {ANNUITY_SPECIALISTS.map((p, i) => (
            <PersonCard key={p.name} person={p} testId={`support-annuity-specialist-${i}`} />
          ))}
        </div>
      </motion.section>

      {/* Carrier contact table */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>
        <div
          className="mb-3 flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-[#0B1437] to-[#1B254B] px-5 py-4"
          data-testid="support-contracting-header"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#F6C561] to-[#D89E2F] shadow-md">
            <Phone className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-['Manrope'] text-base font-extrabold tracking-wide text-white sm:text-lg">
              Contracting-Related: Updates, Support, and Questions
            </h2>
            <p className="text-xs font-medium text-[#F6C561]">
              Reach each carrier directly using the contacts below
            </p>
          </div>
        </div>
        <Card className="overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="support-carrier-table">
              <thead>
                <tr className="bg-[#0B1437]">
                  <th className="px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-white">Carrier / Company</th>
                  <th className="px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-white">Phone</th>
                  <th className="px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-white">Email / Option</th>
                </tr>
              </thead>
              <tbody>
                {CARRIER_CONTACTS.map((c, i) => (
                  <tr
                    key={c.carrier}
                    className={i % 2 === 0 ? 'bg-white' : 'bg-[#F4F7FE]'}
                    data-testid={`support-carrier-row-${i}`}
                  >
                    <td className="px-5 py-3 text-sm font-bold text-[#2B3674]">{c.carrier}</td>
                    <td className="px-5 py-3">
                      <a href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2B3674] hover:text-[#1E3FBF]">
                        <Phone className="h-3.5 w-3.5 text-[#A3AED0]" />
                        {c.phone}
                      </a>
                    </td>
                    <td className="px-5 py-3">
                      <a href={`mailto:${c.email}`} className="text-sm font-semibold text-[#1E3FBF] hover:underline">
                        {c.email}
                      </a>
                      {c.option && (
                        <span className="ml-2 rounded-md bg-[#F6C561]/20 px-2 py-0.5 text-[11px] font-bold text-[#B47E14]">
                          {c.option}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.section>

      {/* 3-Day Rule callout */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#F6C561]/50 bg-[#FFF9EC] px-5 py-4"
        data-testid="support-3day-rule"
      >
        <div className="flex items-center gap-2">
          <AlarmClock className="h-5 w-5 text-[#B47E14]" />
          <span className="font-['Manrope'] text-sm font-extrabold tracking-wide text-[#B47E14]">THE 3-DAY RULE</span>
        </div>
        <p className="flex-1 min-w-[240px] text-sm text-[#2B3674]">
          If no carrier email appears in your inbox or junk folder within 3 days of submission,{' '}
          <span className="font-bold">contact the carrier directly using the information above.</span>
        </p>
      </motion.div>

      {/* Contracting Support */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}>
        <div className="mb-3 flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-[#B47E14]" />
          <h2 className="font-['Manrope'] text-lg font-extrabold tracking-wide text-[#B47E14]">
            CONTRACTING SUPPORT
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {CONTRACTING_SUPPORT.map((p, i) => (
            <PersonCard key={p.name} person={p} testId={`support-contracting-${i}`} />
          ))}
        </div>
      </motion.section>

      {/* General contracting footer */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32 }}
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#0B1437] px-6 py-5"
        data-testid="support-general-contracting"
      >
        <div>
          <h3 className="font-['Manrope'] text-base font-extrabold tracking-wide text-white">
            GENERAL CONTRACTING QUESTIONS
          </h3>
          <p className="text-xs text-white/60">Contact Brandon Beal directly</p>
        </div>
        <a
          href="mailto:bb@breezewealthmanagement.com"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F6C561] to-[#D89E2F] px-5 py-2.5 text-sm font-bold text-[#0B1437] shadow-lg transition-opacity hover:opacity-90"
          data-testid="support-brandon-email"
        >
          <Mail className="h-4 w-4" />
          bb@breezewealthmanagement.com
        </a>
      </motion.div>
    </div>
  );
}
